import { GetAppointment } from "@/services/fhir";

import {
  AppointmentSearchVolumeTooHighError,
  isAppointmentSearchVolumeAboveLimit,
} from "../helpers/appointmentSearchVolumeLimit";
import type { PatientFinderAppointmentMode } from "../helpers/patientFinderAppointmentMode";
import { getAppointmentStatusesForMode } from "../helpers/patientFinderAppointmentMode";

function buildSharedAppointmentParams(params: {
  locationActor: string;
  startDate: string;
  endDate: string;
  mode: PatientFinderAppointmentMode;
}) {
  return {
    scheduling: true,
    status: getAppointmentStatusesForMode(params.mode),
    actor: params.locationActor,
    startDate: params.startDate,
    endDate: params.endDate,
    sort: "date" as const,
  };
}

/**
 * Appointments no intervalo, filtrados por Location (`actor`) e status do modo.
 * 1) `_summary=count` — obtém o total do período.
 * 2) Busca completa com `_count={total}` (sempre tentada, mesmo acima do limite de referência).
 *    Se falhar e o total for alto, propaga mensagem orientando reduzir o intervalo.
 */
export async function fetchLocationAppointments(params: {
  locationActor: string;
  startDate: string;
  endDate: string;
  mode: PatientFinderAppointmentMode;
}): Promise<fhir5.Bundle | undefined> {
  const actor = params.locationActor.trim();
  if (!actor) return undefined;

  const shared = buildSharedAppointmentParams({ ...params, locationActor: actor });

  // Etapa 1 — total no conjunto filtrado (sem _include, mais leve).
  const countBundle = await GetAppointment({
    ...shared,
    summary: true,
  });

  const total = countBundle?.total ?? 0;
  if (total === 0) return undefined;

  // Etapa 2 — sempre tenta a busca completa; mensagem de volume alto só em caso de falha.
  try {
    return await GetAppointment({
      ...shared,
      count: total,
      includePatient: true,
      includeLocation: true,
    });
  } catch (error) {
    if (isAppointmentSearchVolumeAboveLimit(total)) {
      throw new AppointmentSearchVolumeTooHighError(total);
    }
    throw error;
  }
}
